import { Injectable } from "@angular/core";

import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { ServiceMessageBroker, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import {
  BfxFindFilterConditionValueParseType,
  BfxFindDealsFilterFieldName,
  BfxFindOrdersFilterFieldName,
} from "../../index";

import { OrderQueryRequestPrimaryService, QueryResponse } from "./OrderQueryRequestPrimaryService";
import { TradeQueryRequestPrimaryService } from "./TradeQueryRequestPrimaryService";
import { FilterOrderTypes } from "../../model/FilterTypeEnums";

enum QueryResultType {
  Deal,
  Order,
}
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BfxFindFilterDateComparitor } from "shared/cubiq-services/model/find/BfxFindFilterDateComparitor";
import { TranslateService } from "@ngx-translate/core";
import { SysFeatures, SystemConfigPrimaryService } from "shared/auth-services";
import {
  BFX_FIND_TRADE_CLIENT_VALUE,
  BFX_FIND_TRADE_RISK_VALUE,
} from "shared/cubiq-services/model/find/bfx-find-deals/bfx-find-deals.constants";

const FIND_EXTENDEDINFO_SEARCHFORSPAWNED = "searchForSpawnedOrders";
const PARAM_Y = "Y";
const PARAM_TRUE = "true";
const UI_DATE_TIME_PATTERN = "DD/MM/YYYY HH:mm:ss";

// TODO: to be moved to some model package
class DateTimeComparitor {
  timeComparison: CubiqTS.TimeComparison;
  javaTimeZoneCd: string;
  lowerBound: string;
  upperBound: string;
  messageId: string;

  constructor(
    timeComparison?: CubiqTS.TimeComparison,
    javaTimeZoneCd?: string,
    lowerBound?: string,
    upperBound?: string,
    messageId?: string,
  ) {
    this.timeComparison = timeComparison;
    this.javaTimeZoneCd = javaTimeZoneCd;
    this.lowerBound = lowerBound;
    this.upperBound = upperBound;
    this.messageId = messageId;
  }
}

const EXT_RISK_MODE: string = "EXT_RISK_MODE";
const EXT_RISK_MODE_RISK: string = "risk";
const EXT_RISK_MODE_CLIENT: string = "client";

@Injectable()
export class FindActionPrimaryService {
  protected ready: Promise<void>;
  protected broker: ServiceMessageBroker;
  protected shouldSearchInUTC: boolean = true;
  protected maxQueryResult: number = 1001;

  protected dealFieldNameQueryNameMap = new Map([
    [BfxFindDealsFilterFieldName.ACCOUNT_ID, BfxFindDealsFilterFieldName.ACCOUNT_ID],
    [BfxFindDealsFilterFieldName.ACCOUNT_NAME, BfxFindDealsFilterFieldName.ACCOUNT_ID],
    [BfxFindDealsFilterFieldName.CLIENT_ID, BfxFindDealsFilterFieldName.CLIENT_ID],
    [BfxFindDealsFilterFieldName.CLIENT_NAME, BfxFindDealsFilterFieldName.CLIENT_ID],
    [BfxFindDealsFilterFieldName.CLIENT_TRADER, "clientTraderId"],
    [BfxFindDealsFilterFieldName.FIXCODE, "fixingSourceId"],
    // has additional handling (stripping / from string)
    [BfxFindDealsFilterFieldName.INSTRUMENT, "ccyPair"],
    [BfxFindDealsFilterFieldName.CCY_PAIR_GROUP, "ccyPair"],
  ]);

  protected ordersFieldNameQueryNameMap = new Map([
    [BfxFindOrdersFilterFieldName.ACCOUNT_ID, BfxFindOrdersFilterFieldName.ACCOUNT_ID],
    [BfxFindOrdersFilterFieldName.ACCOUNT_NAME, BfxFindOrdersFilterFieldName.ACCOUNT_ID],
    [BfxFindOrdersFilterFieldName.CLIENT_ID, BfxFindOrdersFilterFieldName.CLIENT_ID],
    [BfxFindOrdersFilterFieldName.CLIENT_NAME, BfxFindOrdersFilterFieldName.CLIENT_ID],
    [BfxFindOrdersFilterFieldName.CLIENT_TRADER, "clientTraderId"],
    [BfxFindOrdersFilterFieldName.FIXCODE, "fixingSourceId"],
    [BfxFindOrdersFilterFieldName.BM_FIXCODE, "fixingSourceCd"],
    // has additional handling (stripping / from string)
    [BfxFindOrdersFilterFieldName.INSTRUMENT, "ccyPair"],
    [BfxFindOrdersFilterFieldName.CCY_PAIR_GROUP, "ccyPair"],
    [BfxFindOrdersFilterFieldName.TYPE, "requestedOrderType"],
    [BfxFindOrdersFilterFieldName.PRODUCT, "orderProduct"],
  ]);

  constructor(
    protected factory: ServiceMessageBrokerFactory,
    protected orderQuery: OrderQueryRequestPrimaryService,
    protected tradeQuery: TradeQueryRequestPrimaryService,
    protected translationService: TranslateService,
    protected systemConfigService: SystemConfigPrimaryService,
  ) {
    this.systemConfigService.getSystemConfig(SysFeatures.maxQueryResult).then((result) => {
      let maxQueryResult = Number.parseInt(result);

      if (maxQueryResult > 0) {
        // request one extra item to track if max exceeded
        this.maxQueryResult = maxQueryResult + 1;
      }
    });
  }

  public findDeals(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        let query = this.parseData(data, new CubiqTS.TradeQueryRequestImpl());
        this.tradeQuery.requestTradeData(query).then(
          (searchResult) => {
            logger.out.info("FindActionService: Processing deal search results");
            let captionLabel = "No results returned";
            let found = [];

            captionLabel = this.processSearchResult(searchResult, found);

            resolve({
              results: found,
              captionLabel: captionLabel,
            });
          },
          (err) => {
            logger.out.info(`FindActionService: Deal search failure: ${err}`);
            reject(err);
          },
        );
      } catch (error) {
        logger.out.warn(`Error raised during find deal: ${error}`);
        reject(error);
      }
    });
  }

  public findOrders(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let query = this.parseData(data, new CubiqTS.OrderQueryRequestImpl());

      this.orderQuery.requestOrderData(query).then(
        (searchResult) => {
          logger.out.info("FindActionService: Processing order search results");
          let captionLabel = "No results returned";
          let found = [];

          captionLabel = this.processSearchResult(searchResult, found);

          resolve({
            results: found,
            captionLabel: captionLabel,
          });
        },
        (err) => {
          reject(err);
        },
      );
    });
  }

  processSearchResult(searchResult: QueryResponse, found: Array<any>): string {
    logger.out.info("FindActionService: Processing search results");
    let captionLabel: string = "";
    if (searchResult == null) {
      // NOP
      logger.out.warn("FindActionService: Null search results");
    } else if (searchResult.isError) {
      logger.out.warn("FindActionService: Error in search results");
      let errors: CubiqTS.ErrorsImpl = searchResult.errors;
      if (errors != null && errors.errorMessages != null && errors.errorMessages.length > 0) {
        let errorObject: any = JSON.parse(errors.errorMessages[0] + "");
        captionLabel = this.translationService.instant(errorObject.id, errorObject.args);
      }
    } else {
      found.push(...searchResult.found);

      let numResults = found != null ? found.length : 0;
      logger.out.info(`FindActionService: found ${numResults}`);

      let displayResults = this.maxQueryResult - 1;

      if (numResults > displayResults) {
        // remove 1001st item for displayed result of 1000 items
        logger.out.info("FindActionService: max results exceeded - truncating");
        found.splice(-1, 1);
      }

      if (numResults > displayResults || searchResult.maxResultsReached === true) {
        captionLabel =
          "1-" +
          Math.min(numResults, displayResults) +
          " of many results returned. Please consider refining your search.";
      } else if (numResults === 0) {
        captionLabel = "0 results returned. Please consider refining your search.";
      } else {
        captionLabel = "";
      }
    }
    return captionLabel;
  }

  protected getQueryRuleDedupKey(fieldName, operator: CubiqTS.Operator) {
    if (fieldName && CubiqTS.Operator[operator]) {
      return `{${fieldName}-${CubiqTS.Operator[operator]}`;
    }

    return null;
  }

  protected parseArrayLikeString(values: string): Array<any> {
    if (typeof values === "string") {
      // TODO i18n split char value
      let valuesArray = values.split(",");

      if (valuesArray.length > 0) {
        return valuesArray
          .filter((value) => {
            return typeof value === "string" && value.length > 0;
          })
          .map((value) => {
            return {
              value: value.trim(),
            };
          });
      }
    }

    return [];
  }

  protected parseValues(
    condition: any,
    dedupFieldName: any,
    dedupValueSet: any,
    isEqualDay: boolean = false,
  ): any {
    let values = null;
    let isArray = Array.isArray(condition.value);
    let isArrayLike =
      BfxFindFilterConditionValueParseType.ExclusiveRefString === condition.parseType ||
      BfxFindFilterConditionValueParseType.RefString === condition.parseType;
    let isInnerArray =
      BfxFindFilterConditionValueParseType.InnerArray === condition.parseType && isArray;

    if (isArray || isArrayLike) {
      values = isInnerArray
        ? condition.value.map((a) => {
            return a.value;
          })
        : isArrayLike
        ? [this.parseArrayLikeString(condition.value)]
        : [condition.value];
      if (
        condition.fieldName == BfxFindOrdersFilterFieldName.TYPE ||
        condition.fieldName == BfxFindDealsFilterFieldName.TYPE
      ) {
        return condition.value;
      }
      values = values.reduce((p, c) => {
        let n = !Array.isArray(c)
          ? []
          : c
              .filter((entry) => {
                if (!dedupFieldName) {
                  return true;
                }
                if (!entry.value) {
                  // Sometimes null values are restored from old filters so make sure not sent in server query as
                  //Cubiq does not support sending null elements inside list of strings
                  return false;
                }

                if (dedupValueSet.has(JSON.stringify(entry))) {
                  return false;
                }

                dedupValueSet.add(JSON.stringify(entry));
                return true;
              })
              .map((entry) => {
                if (entry.value instanceof Date) {
                  return CubiqTS.moment(entry.value).format("YYYY-MM-DD");
                  // } else if (entry.value instanceof CubiqTS.Long) {
                  //     return entry.value;
                } else if (typeof entry.value !== "string") {
                  return entry.value.toString();
                }

                return entry.value;
              });

        return p.concat(n);
      }, []);
      // TODO: whole code block below to be optimized
    } else if (condition.from && condition.to) {
      if (condition.from instanceof Date && condition.to instanceof Date) {
        let fromDateValue = CubiqTS.moment(condition.from);
        let toDateValue = CubiqTS.moment(condition.to).add(1, "days").subtract(1, "milliseconds");
        let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
        let formattedFromDate = fromDateValue.format(UI_DATE_TIME_PATTERN);
        let formattedToDate = toDateValue.format(UI_DATE_TIME_PATTERN);
        let dateTimeComparitor = new DateTimeComparitor(
          null,
          timezoneCd,
          formattedFromDate,
          formattedToDate,
          "",
        );
        values = [JSON.stringify(dateTimeComparitor)];
      } else if (typeof condition.from !== "string" || typeof condition.to !== "string") {
        // NOTE: this probably don't apply since relative dates are disabled on the between operator
        values = [condition.from.toString(), condition.to.toString()];
      } else {
        values = [condition.from, condition.to];
      }
    } else if (isEqualDay && condition.value instanceof Date) {
      let dateValue = CubiqTS.moment(condition.value);
      let upperBoundValue = CubiqTS.moment(condition.value)
        .add(1, "days")
        .subtract(1, "milliseconds");
      let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
      let formattedDate = dateValue.format(UI_DATE_TIME_PATTERN);
      let formattedUpperBoundDate = upperBoundValue.format(UI_DATE_TIME_PATTERN);
      let dateTimeComparitor = new DateTimeComparitor(
        null,
        timezoneCd,
        formattedDate,
        formattedUpperBoundDate,
        "",
      );
      values = [JSON.stringify(dateTimeComparitor)];
    } else if (
      !isEqualDay &&
      condition.value instanceof Date &&
      condition.operator === CubiqTS.Operator.Greater
    ) {
      let dateValue = CubiqTS.moment(condition.value).add(1, "days").subtract(1, "milliseconds");
      let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
      let formattedDate = dateValue.format(UI_DATE_TIME_PATTERN);
      let dateTimeComparitor = new DateTimeComparitor(null, timezoneCd, formattedDate, null, "");
      values = [JSON.stringify(dateTimeComparitor)];
    } else if (
      !isEqualDay &&
      condition.value instanceof Date &&
      condition.operator === CubiqTS.Operator.LessOrEqual
    ) {
      let dateValue = CubiqTS.moment(condition.value).add(1, "days").subtract(1, "milliseconds");
      let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
      let formattedDate = dateValue.format(UI_DATE_TIME_PATTERN);
      let dateTimeComparitor = new DateTimeComparitor(null, timezoneCd, null, formattedDate, "");
      values = [JSON.stringify(dateTimeComparitor)];
    } else if (
      !isEqualDay &&
      condition.value instanceof Date &&
      condition.operator === CubiqTS.Operator.GreaterOrEqual
    ) {
      let dateValue = CubiqTS.moment(condition.value);
      let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
      let formattedDate = dateValue.format(UI_DATE_TIME_PATTERN);
      let dateTimeComparitor = new DateTimeComparitor(null, timezoneCd, formattedDate, null, "");
      values = [JSON.stringify(dateTimeComparitor)];
    } else if (!isEqualDay && condition.value instanceof Date) {
      // CubiqTS.Operator.Less
      let dateValue = CubiqTS.moment(condition.value);
      let timezoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
      let formattedDate = dateValue.format(UI_DATE_TIME_PATTERN);
      let dateTimeComparitor = new DateTimeComparitor(null, timezoneCd, null, formattedDate, "");
      values = [JSON.stringify(dateTimeComparitor)];
    } else if (isEqualDay && typeof condition.value === "string") {
      if (condition.fieldName.length != 0) {
        values = [JSON.stringify(this.determineTimeComparitorValue(condition))];
      } else {
        values = [];
      }
    } else if (typeof condition.value !== "string") {
      values = [condition.value.toString()];
    } else {
      values = [condition.value];
    }
    return values;
  }

  private determineTimeComparitorValue(condition: any): DateTimeComparitor {
    let value = condition.value;
    let dateTimeComparitor: DateTimeComparitor = new DateTimeComparitor(null, null, null, null, "");
    if (value != null) {
      dateTimeComparitor.timeComparison = BfxFindFilterDateComparitor.toTimeComparison(value);
      dateTimeComparitor.javaTimeZoneCd = this.shouldSearchInUTC ? null : this.getLocalTimezone();
    }

    return dateTimeComparitor;
  }

  private getLocalTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  protected parseQueryRuleData(
    condition: any,
    dedupFieldName: any,
    dedupValueSet: any,
    dedupFieldNameValueSetMap,
    dedupFieldNameQueryRuleMap: Map<string, CubiqTS.QueryRuleImpl>,
    fieldNameQueryNameMap,
  ) {
    let qr: CubiqTS.QueryRuleImpl;
    let add = true;
    let isEqualDay =
      BfxFindFilterConditionValueParseType.DateEquality === condition.parseType &&
      (condition.operator == CubiqTS.Operator.Equal ||
        condition.operator == CubiqTS.Operator.NotEqual);

    if (
      fieldNameQueryNameMap.has(condition.fieldName) &&
      BfxFindFilterConditionValueParseType.RefString != condition.parseType
    ) {
      dedupFieldName = fieldNameQueryNameMap.get(condition.fieldName);

      let dedupKey = this.getQueryRuleDedupKey(dedupFieldName, condition.operator);

      if (!dedupFieldNameValueSetMap.has(dedupKey)) {
        dedupValueSet = new Set();
        qr = new CubiqTS.QueryRuleImpl();
        qr.values = [];
        dedupFieldNameValueSetMap.set(dedupKey, dedupValueSet);
        dedupFieldNameQueryRuleMap.set(dedupKey, qr);
      } else {
        dedupValueSet = dedupFieldNameValueSetMap.get(dedupKey);
        qr = dedupFieldNameQueryRuleMap.get(dedupKey);
        add = false;
      }
    } else if (
      BfxFindFilterConditionValueParseType.RefString == condition.parseType ||
      BfxFindFilterConditionValueParseType.RefArray == condition.parseType
    ) {
    } else {
      qr = new CubiqTS.QueryRuleImpl();
      qr.values = [];
    }

    qr.field = dedupFieldName ? dedupFieldName : condition.fieldName;

    if (
      isEqualDay &&
      typeof condition.value !== "string" &&
      condition.operator == CubiqTS.Operator.Equal
    ) {
      qr.operator = CubiqTS.Operator.Between;
    } else if (
      isEqualDay &&
      typeof condition.value !== "string" &&
      condition.operator == CubiqTS.Operator.NotEqual
    ) {
      qr.operator = CubiqTS.Operator.NotBetween;
    } else {
      qr.operator = condition.operator;
    }

    qr.values = qr.values.concat(
      this.parseValues(condition, dedupFieldName, dedupValueSet, isEqualDay),
    );

    return {
      qr: qr,
      add: add,
    };
  }

  protected parseRefQueryRules(
    condition: any,
    dedupFieldName: any,
    dedupValueSet: any,
    dedupFieldNameValueSetMap,
    dedupFieldNameQueryRuleMap,
  ) {
    let refValues = this.parseValues(condition, dedupFieldName, dedupValueSet);

    let refQr = refValues.map((ref) => {
      let qr = new CubiqTS.QueryRuleImpl();
      qr.field = condition.fieldName;
      qr.operator = condition.operator;
      qr.values = [ref];
      return qr;
    });

    return refQr;
  }

  parseData(data: any, targetQuery: CubiqTS.OrderQueryRequestImpl): CubiqTS.OrderQueryRequestImpl;
  parseData(data: any, targetQuery: CubiqTS.TradeQueryRequestImpl): CubiqTS.TradeQueryRequestImpl;
  parseData(
    data: any,
    targetQuery: CubiqTS.OrderQueryRequestImpl | CubiqTS.TradeQueryRequestImpl,
  ): typeof targetQuery {
    if (targetQuery == null) {
      throw new Error(
        "Illegal Argument: targetQuery must be either CubiqTS.OrderQueryRequestImpl or CubiqTS.TradeQueryRequestImpl",
      );
    }

    let qg = new CubiqTS.QueryGroupImpl();
    let hasExclusiveRef = false;

    let orderTypeFieldName;
    let baseField: string;
    let nonBaseField: string;
    let queryResultType: QueryResultType;
    let fieldNameQueryNameMap;

    if (targetQuery instanceof CubiqTS.TradeQueryRequestImpl) {
      orderTypeFieldName = BfxFindDealsFilterFieldName.TYPE;
      baseField = "baseCcyCd";
      nonBaseField = "nonBaseCcyCd";
      queryResultType = QueryResultType.Deal;
      fieldNameQueryNameMap = this.dealFieldNameQueryNameMap;
    } else if (targetQuery instanceof CubiqTS.OrderQueryRequestImpl) {
      orderTypeFieldName = BfxFindOrdersFilterFieldName.TYPE;
      baseField = "baseCode";
      nonBaseField = "nonBaseCode";
      queryResultType = QueryResultType.Order;
      fieldNameQueryNameMap = this.ordersFieldNameQueryNameMap;
    }

    qg.condition = CubiqTS.Condition.AND;
    qg.rules = [];
    qg.groups = [];

    let dedupFieldNameValueSetMap = new Map<string, Set<any>>();
    let dedupFieldNameQueryRuleMap = new Map<string, CubiqTS.QueryRuleImpl>();

    let showSpawnedOrders: boolean = false;
    data.forEach((headingData) => {
      headingData.filterConditions.forEach((condition) => {
        if (
          (condition.operator !== null && condition.value != null) ||
          (condition.from != null && condition.to != null)
        ) {
          let dedupValueSet: Set<any> | null = null;
          let dedupFieldName: string | null = null;

          if (condition.fieldName === BfxFindOrdersFilterFieldName.BM_SPAWNED) {
            // we want this param regardless of what else was chosen
            // we don't want it in the query
            // if it is true - then we track it -and later hide it in the extendedInfo of the request
            let inrVal =
              condition.value != null && condition.value[0] != null
                ? condition.value[0].value
                : false;
            if (PARAM_TRUE == inrVal) {
              showSpawnedOrders = true;
            }
          } else if (
            !hasExclusiveRef &&
            BfxFindFilterConditionValueParseType.ExclusiveRefString == condition.parseType
          ) {
            let qgRef = new CubiqTS.QueryGroupImpl();
            let rules = this.parseRefQueryRules(
              condition,
              dedupFieldName,
              dedupValueSet,
              dedupFieldNameValueSetMap,
              dedupFieldNameQueryRuleMap,
            );

            if (CubiqTS.Operator.NotEqual === condition.operator) {
              qgRef.condition = CubiqTS.Condition.AND;
            } else {
              qgRef.condition = CubiqTS.Condition.OR;
            }

            qgRef.groups = [];
            qgRef.rules = rules;

            qg.groups.push(qgRef);
            hasExclusiveRef = true;
          } else if (
            !hasExclusiveRef &&
            (BfxFindFilterConditionValueParseType.RefString == condition.parseType ||
              BfxFindFilterConditionValueParseType.RefArray == condition.parseType)
          ) {
            let rules = this.parseRefQueryRules(
              condition,
              dedupFieldName,
              dedupValueSet,
              dedupFieldNameValueSetMap,
              dedupFieldNameQueryRuleMap,
            );
            let qgRef = new CubiqTS.QueryGroupImpl();

            if (CubiqTS.Operator.NotEqual === condition.operator) {
              qgRef.condition = CubiqTS.Condition.AND;
            } else {
              qgRef.condition = CubiqTS.Condition.OR;
            }

            qgRef.groups = [];
            qgRef.rules = rules;

            qg.groups.push(qgRef);
          } else if (!hasExclusiveRef) {
            let qrData = this.parseQueryRuleData(
              condition,
              dedupFieldName,
              dedupValueSet,
              dedupFieldNameValueSetMap,
              dedupFieldNameQueryRuleMap,
              fieldNameQueryNameMap,
            );

            if (qrData.add && qrData.qr.values.length > 0) {
              // Sales group handling
              if (qrData.qr.field == BfxFindOrdersFilterFieldName.SALES_GROUP) {
                if (qrData.qr.operator == CubiqTS.Operator.Equal) {
                  qrData.qr.operator = CubiqTS.Operator.In;
                } else if (qrData.qr.operator == CubiqTS.Operator.NotEqual) {
                  qrData.qr.operator = CubiqTS.Operator.NotIn;
                }
              }

              if (
                qrData.qr.field !== "ccy" &&
                qrData.qr.field !== BfxFindDealsFilterFieldName.TYPE &&
                qrData.qr.field !== BfxFindDealsFilterFieldName.TRADE_TYPE &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.EXPIRED_DATE &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.TYPE &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.MODIFY_USER &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.REQUEST_RT &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.FIXING_DATE &&
                qrData.qr.field !== "fixingSourceId" &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.BM_SPAWNED &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.SEARCH_IN_TZ &&
                qrData.qr.field !== BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST
              ) {
                qg.rules.push(qrData.qr);
              } else if (qrData.qr.field == BfxFindOrdersFilterFieldName.SEARCH_IN_TZ) {
                if (qrData.qr.values && qrData.qr.values.length > 0) {
                  this.shouldSearchInUTC = qrData.qr.values[0] == PARAM_TRUE;
                }
              } else if (
                qrData.qr.field == BfxFindDealsFilterFieldName.TYPE ||
                qrData.qr.field == BfxFindOrdersFilterFieldName.TYPE
              ) {
                let qgType = this.createOrderTypeGroup(orderTypeFieldName, qrData.qr, condition);

                qg.groups.push(qgType);
              } else if (qrData.qr.field == BfxFindOrdersFilterFieldName.MODIFY_USER) {
                qrData.qr.values = [
                  JSON.stringify({
                    modifyUserIds: qrData.qr.values,
                  }),
                ];
                qrData.qr.operator = CubiqTS.Operator.In;
                qg.rules.push(qrData.qr);
              } else if (qrData.qr.field == BfxFindOrdersFilterFieldName.EXPIRED_DATE) {
                let qgExpiry = new CubiqTS.QueryGroupImpl();
                let qrExpiryState = new CubiqTS.QueryRuleImpl();

                qrExpiryState.field = BfxFindOrdersFilterFieldName.ORDER_STATE;
                qrExpiryState.values = [CubiqTS.OrderState[CubiqTS.OrderState.EXPIRED]];
                qrExpiryState.operator = CubiqTS.Operator.Equal;

                qgExpiry.condition = CubiqTS.Condition.AND;
                qgExpiry.groups = [];
                qgExpiry.rules = [qrData.qr, qrExpiryState];
                qg.groups.push(qgExpiry);
              } else if (qrData.qr.field == "ccy") {
                // split ccy into base and non-base
                let qgCcy = new CubiqTS.QueryGroupImpl();
                let qrBase = new CubiqTS.QueryRuleImpl();
                let qrNonBase = new CubiqTS.QueryRuleImpl();

                if (CubiqTS.Operator.NotIn === qrData.qr.operator) {
                  qgCcy.condition = CubiqTS.Condition.AND;
                } else {
                  qgCcy.condition = CubiqTS.Condition.OR;
                }

                qrBase.field = baseField;
                qrBase.values = qrData.qr.values;
                qrBase.operator = qrData.qr.operator;
                qrNonBase.field = nonBaseField;
                qrNonBase.values = qrData.qr.values;
                qrNonBase.operator = qrData.qr.operator;

                qgCcy.groups = [];
                qgCcy.rules = [qrBase, qrNonBase];

                qg.groups.push(qgCcy);
              } else if (
                qrData.qr.field == BfxFindOrdersFilterFieldName.REQUEST_RT ||
                qrData.qr.field === BfxFindOrdersFilterFieldName.FIXING_DATE
              ) {
                if (
                  qrData.qr.operator === CubiqTS.Operator.NotIn ||
                  qrData.qr.operator === CubiqTS.Operator.NotBetween
                  // || (qrData.qr.operator == CubiqTS.Operator.Equal && qrData.qr.values == 0) - Alex - this clause should always be false but not sure what it was aiming for
                ) {
                  let qGroup = new CubiqTS.QueryGroupImpl();
                  qGroup.condition = CubiqTS.Condition.OR;
                  qGroup.groups = [];
                  qGroup.rules = [qrData.qr, this.nullValuesQueryRule(qrData.qr.field)];
                  qg.groups.push(qGroup);
                } else {
                  qg.rules.push(qrData.qr);
                }
              } else if (qrData.qr.field == "fixingSourceId") {
                let customFieldIndex: number = qrData.qr.values.indexOf("-1");
                let fixingSrcGroup = new CubiqTS.QueryGroupImpl();
                fixingSrcGroup.groups = [];

                if (customFieldIndex !== -1) {
                  qrData.qr.values.splice(customFieldIndex, 1);
                  if (qrData.qr.values.length > 0) {
                    let mixedGroup = new CubiqTS.QueryGroupImpl();
                    mixedGroup.condition = CubiqTS.Condition.AND;
                    mixedGroup.groups = [];
                    mixedGroup.rules = [
                      this.ndfProductRule(qrData.qr.operator),
                      this.nullValuesQueryRule(qrData.qr.field),
                    ];
                    fixingSrcGroup.groups.push(mixedGroup);
                    fixingSrcGroup.condition = CubiqTS.Condition.OR;
                    fixingSrcGroup.rules = [qrData.qr];
                  } else {
                    fixingSrcGroup.condition =
                      CubiqTS.Operator.NotIn === qrData.qr.operator
                        ? CubiqTS.Condition.OR
                        : CubiqTS.Condition.AND;
                    fixingSrcGroup.rules =
                      qrData.qr.operator === CubiqTS.Operator.In
                        ? [
                            this.ndfProductRule(qrData.qr.operator),
                            this.nullValuesQueryRule(qrData.qr.field),
                          ]
                        : [
                            this.ndfProductRule(qrData.qr.operator),
                            this.notNullValuesQueryRule(qrData.qr.field),
                          ];
                  }
                } else {
                  fixingSrcGroup.condition = CubiqTS.Condition.OR;
                  fixingSrcGroup.rules =
                    qrData.qr.operator === CubiqTS.Operator.In
                      ? [qrData.qr]
                      : [qrData.qr, this.nullValuesQueryRule(qrData.qr.field)];
                }
                qg.groups.push(fixingSrcGroup);
              } else if (BfxFindOrdersFilterFieldName.BM_FORWARD_FIRST == qrData.qr.field) {
                this.processForwardFirstFilter(targetQuery, qrData, qg);
              } else if (BfxFindDealsFilterFieldName.TRADE_TYPE == qrData.qr.field) {
                this.processTradeTypeFilter(targetQuery, qrData, qg);
              }
            }
          } else {
            logger.out.warn(
              `${this.constructor.name} dropping field name ${condition.fieldName} due to exclusive refs `,
            );
          }
        }
      });
    });

    if (showSpawnedOrders) {
      targetQuery.extendedInfo.set(FIND_EXTENDEDINFO_SEARCHFORSPAWNED, PARAM_Y);
    }
    if (targetQuery instanceof CubiqTS.OrderQueryRequestImpl) {
      targetQuery.queryGroup = qg;
      targetQuery.maxTotalResults = this.maxQueryResult;
      return targetQuery;
    } else if (targetQuery instanceof CubiqTS.TradeQueryRequestImpl) {
      targetQuery.queryGroup = qg;
      targetQuery.maxTotalResults = this.maxQueryResult;

      try {
        this.processTradeQuery(targetQuery);
      } catch (e) {
        logger.out.error(`Failed to post process trade query ${e}`);
      }

      return targetQuery;
    }
  }
  processTradeTypeFilter(
    targetQuery: CubiqTS.TradeQueryRequest | CubiqTS.OrderQueryRequestImpl,
    qrData: { qr: CubiqTS.QueryRuleImpl; add: boolean },
    qg: CubiqTS.QueryGroupImpl,
  ) {
    let riskTradeType: boolean = null;

    let queryValue = qrData.qr.values[0];
    let flip: boolean = CubiqTS.Operator.NotEqual == qrData.qr.operator;
    if (null == queryValue || queryValue == "") {
      // NOP
    } else {
      if (BFX_FIND_TRADE_RISK_VALUE == queryValue) {
        // risk it is
        riskTradeType = flip ? false : true;
      } else if (BFX_FIND_TRADE_CLIENT_VALUE == queryValue) {
        // risk it is not
        riskTradeType = flip ? true : false;
      } else {
        // NOP
        riskTradeType = null;
      }
      this.processRiskTrades(targetQuery, riskTradeType);
    }
  }

  processForwardFirstFilter(
    targetQuery: CubiqTS.TradeQueryRequest | CubiqTS.OrderQueryRequestImpl,
    qrData: { qr: CubiqTS.QueryRuleImpl; add: boolean },
    qg: CubiqTS.QueryGroupImpl,
  ) {
    let queryValue = qrData.qr.values[0];
    if (null == queryValue || queryValue == "") {
      // NOP
    } else {
      let fwdFirstRule = null;

      if ("true" == queryValue) {
        // include FwdFirst
        fwdFirstRule = this.notNullValuesQueryRule(BfxFindOrdersFilterFieldName.REQ_QUOTE_ID);
      } else if ("false" == queryValue) {
        // don't include FwdFirst
        fwdFirstRule = this.nullValuesQueryRule(BfxFindOrdersFilterFieldName.REQ_QUOTE_ID);
      } else {
        // dont include query clause at all
        fwdFirstRule = null;
      }

      if (null != fwdFirstRule) {
        qg.rules.push(fwdFirstRule);
      }
    }
  }

  /**
   *
   * @param orderTypeFieldName
   * @param qr  This is a QueryRuleImpl but values are not strings
   * @param condition
   * @returns
   */
  private createOrderTypeGroup(orderTypeFieldName: string, qr: any, condition: any) {
    /** Hold all the selected Algo types*/
    const qrAlgo = new CubiqTS.QueryRuleImpl();
    qrAlgo.field = BfxFindOrdersFilterFieldName.ALGO_STRATEGY_TYPE;
    qrAlgo.operator = qr.operator;
    qrAlgo.values = [];

    /** Hold all selected limit types */
    const qrLimits = new CubiqTS.QueryRuleImpl();
    qrLimits.field = orderTypeFieldName;
    qrLimits.operator = qr.operator;
    qrLimits.values = [];

    const bmSet = new Set<FilterOrderTypes>();

    // So first partition types into BMs, Algos, or Limits
    qr.values.forEach((val) => {
      // val should have have {type:number, value:string} structure
      if (val.type === FilterOrderTypes.ALGO || val.type === FilterOrderTypes.BMALGO) {
        qrAlgo.values.push(val.value);
      } else if (val.type === FilterOrderTypes.BMARK || val.type === FilterOrderTypes.BMHO) {
        bmSet.add(val.type);
      } else if (val.value) {
        qrLimits.values.push(val.value.toString());
        if (CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.SL_ATRATE] === val.value) {
          qrLimits.values.push(CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.SL_ASK]);
          qrLimits.values.push(CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.SL_BID]);
        } else if (CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.TSL] === val.value) {
          qrLimits.values.push(CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.TSL_ASK]);
          qrLimits.values.push(CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.TSL_BID]);
        }
      }
    });

    let groups = [];
    if (bmSet.size > 0) {
      // BMs can be a bit tricky as they may need to include or exclude BMHOs
      groups = this.getBMOrderTypeGroup(condition.operator, bmSet, orderTypeFieldName);
    }
    if (qrAlgo.values.length > 0) {
      const innerGroup = new CubiqTS.QueryGroupImpl();
      innerGroup.condition = CubiqTS.Condition.OR;
      innerGroup.rules = [qrAlgo];
      if (condition.operator === CubiqTS.Operator.NotIn) {
        // For Not in, then we match if name not matched or algoName is null
        innerGroup.rules.push(
          this.nullValuesQueryRule(BfxFindOrdersFilterFieldName.ALGO_STRATEGY_TYPE),
        );
      }
      groups.push(innerGroup);
    }

    if (qrLimits.values.length > 0) {
      let qgInnerType = new CubiqTS.QueryGroupImpl();
      qgInnerType.rules = [qrLimits];
      groups.push(qgInnerType);
    }

    let retGroup = new CubiqTS.QueryGroupImpl();
    retGroup.rules = [];
    retGroup.groups = groups;
    // If Not In, then order must be in all Not in groups
    retGroup.condition =
      condition.operator === CubiqTS.Operator.NotIn ? CubiqTS.Condition.AND : CubiqTS.Condition.OR;

    if (condition.operator === CubiqTS.Operator.NotIn && qrAlgo.values.length == 0) {
      // Algos are not returned by default on BM or Limit searches if not explicitly listed
      // so add an OR  is ALGO
      let outerType = new CubiqTS.QueryGroupImpl();
      outerType.condition = CubiqTS.Condition.OR;
      outerType.groups = [retGroup];
      outerType.rules = [
        this.notNullValuesQueryRule(BfxFindOrdersFilterFieldName.ALGO_STRATEGY_TYPE),
      ];
      retGroup = outerType;
    }

    return retGroup;
  }

  /**
   * For 'In' Bm types we use:
   * BM => requestOrderType = BMARK && bmOrderType == STD
   * BMHO = requestOrder type = BMARK | BMALGO && bmOrderType = Position
   *
   * For 'Not In'  Bm Types we use:
   * BM => requestOrderType != BMARK || bmOrderType != STD
   * BMHO => requestOrderType != BMARK | BMALGO || bmOrderType != Position
   */
  private getBMOrderTypeGroup(
    operator: CubiqTS.Operator,
    bmTypes: Set<FilterOrderTypes>,
    orderTypeFieldName: string,
  ): CubiqTS.QueryGroupImpl[] {
    const groups: CubiqTS.QueryGroupImpl[] = [];

    if (bmTypes.has(FilterOrderTypes.BMARK)) {
      const typeGroup = new CubiqTS.QueryGroupImpl();

      // Rule for Order type
      const typeRule = new CubiqTS.QueryRuleImpl();
      typeRule.field = orderTypeFieldName;
      typeRule.values = [CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.BMARK]];
      typeRule.operator = operator;

      // Rule for bmOrderType
      const bmTypeRule = new CubiqTS.QueryRuleImpl();
      bmTypeRule.field = BfxFindOrdersFilterFieldName.BM_ORDER_TYPE;
      bmTypeRule.values = [CubiqTS.BenchmarkOrderType[CubiqTS.BenchmarkOrderType.Standard]];
      bmTypeRule.operator = operator;

      typeGroup.groups = [];
      typeGroup.rules = [typeRule, bmTypeRule];
      typeGroup.condition =
        operator == CubiqTS.Operator.In ? CubiqTS.Condition.AND : CubiqTS.Condition.OR;
      groups.push(typeGroup);
    }

    if (bmTypes.has(FilterOrderTypes.BMHO)) {
      const typeGroup = new CubiqTS.QueryGroupImpl();

      // Rule for Order type
      const typeRule = new CubiqTS.QueryRuleImpl();
      typeRule.field = orderTypeFieldName;
      typeRule.values = [
        CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.BMARK],
        // CubiqTS.RequestOrderType[CubiqTS.RequestOrderType.BMALGO],
      ];
      typeRule.operator = operator;

      // Rule for bmOrderType
      const bmTypeRule = new CubiqTS.QueryRuleImpl();
      bmTypeRule.field = BfxFindOrdersFilterFieldName.BM_ORDER_TYPE;
      bmTypeRule.values = [CubiqTS.BenchmarkOrderType[CubiqTS.BenchmarkOrderType.PositionOrder]];
      bmTypeRule.operator = operator;

      typeGroup.groups = [];
      typeGroup.rules = [typeRule, bmTypeRule];
      typeGroup.condition =
        operator == CubiqTS.Operator.In ? CubiqTS.Condition.AND : CubiqTS.Condition.OR;
      groups.push(typeGroup);
    }

    return groups;
  }

  private processTradeQuery(query: CubiqTS.TradeQueryRequestImpl) {
    this.processSpotReserve(query.queryGroup);
  }

  private processSpotReserve(queryGroup: CubiqTS.QueryGroupImpl): void {
    if (queryGroup.groups != null && Array.isArray(queryGroup.groups)) {
      queryGroup.groups.forEach((qg) => this.processSpotReserve(qg));
    }

    if (queryGroup.rules != null && Array.isArray(queryGroup.rules)) {
      let productRule: CubiqTS.QueryRule = queryGroup.rules.find(
        (qr) => qr.field == "product" && Array.isArray(qr.values) && qr.values.length > 0,
      );

      if (productRule != null) {
        queryGroup.rules.splice(queryGroup.rules.indexOf(productRule), 1);

        let productIn: boolean = productRule.operator == CubiqTS.Operator.In;
        let productGroup: CubiqTS.QueryGroupImpl = new CubiqTS.QueryGroupImpl();
        productGroup.condition = productIn ? CubiqTS.Condition.OR : CubiqTS.Condition.AND;
        productGroup.rules = [];

        productRule.values.forEach((value: string) => {
          if (value === "SPT" || value === "SpotReserve") {
            let isSpotReserve: boolean = value === "SpotReserve";
            let fwdGroup: CubiqTS.QueryGroupImpl = new CubiqTS.QueryGroupImpl();
            fwdGroup.condition = productIn ? CubiqTS.Condition.AND : CubiqTS.Condition.OR;

            let productRule: CubiqTS.QueryRuleImpl = new CubiqTS.QueryRuleImpl();
            productRule.field = "product";
            productRule.operator = productIn ? CubiqTS.Operator.Equal : CubiqTS.Operator.NotEqual;
            productRule.values = ["SPT"];

            let spotReserveRule: CubiqTS.QueryRuleImpl = new CubiqTS.QueryRuleImpl();
            spotReserveRule.field = "spotReservationFg";
            spotReserveRule.operator = CubiqTS.Operator.Equal;
            spotReserveRule.values = [isSpotReserve == productIn ? "true" : "false"];

            fwdGroup.rules = [productRule, spotReserveRule];

            if (!Array.isArray(productGroup.groups)) {
              productGroup.groups = [];
            }

            productGroup.groups.push(fwdGroup);
          } else {
            let rule: CubiqTS.QueryRuleImpl = new CubiqTS.QueryRuleImpl();
            rule.field = "product";
            rule.operator = productRule.operator;
            rule.values = [value];
            productGroup.rules.push(rule);
          }
        });

        if (!Array.isArray(queryGroup.groups)) {
          queryGroup.groups = [];
        }

        queryGroup.groups.push(productGroup);
      }
    }
  }

  processRiskTrades(query: CubiqTS.TradeQueryRequestImpl, riskTrades: boolean) {
    if (null != riskTrades) {
      query.extendedInfo.set(EXT_RISK_MODE, riskTrades ? EXT_RISK_MODE_RISK : EXT_RISK_MODE_CLIENT);
    }
  }

  private nullValuesQueryRule(fieldName: any): CubiqTS.QueryRuleImpl {
    let nullValuesRule: CubiqTS.QueryRuleImpl = new CubiqTS.QueryRuleImpl();
    nullValuesRule.field = fieldName;
    nullValuesRule.operator = CubiqTS.Operator.IsNull;
    nullValuesRule.values = [];
    return nullValuesRule;
  }

  private notNullValuesQueryRule(fieldName: any): CubiqTS.QueryRuleImpl {
    let notNullValuesRule: CubiqTS.QueryRuleImpl = new CubiqTS.QueryRuleImpl();
    notNullValuesRule.field = fieldName;
    notNullValuesRule.operator = CubiqTS.Operator.IsNotNull;
    notNullValuesRule.values = [];
    return notNullValuesRule;
  }

  private ndfProductRule(operator: CubiqTS.Operator): CubiqTS.QueryRuleImpl {
    let qrNdfType = new CubiqTS.QueryRuleImpl();
    qrNdfType.field = "product";
    qrNdfType.operator = operator;
    qrNdfType.values = [CubiqTS.Product[CubiqTS.Product.NDF]];
    return qrNdfType;
  }
}
